import { clientAccess } from '@/services/clientAccess';
import { fetchTraceText } from '@/services/conversationExportService';
import { compactAssistantRuns, parseTraceLine } from '@/utils/traceParsing';
import type { Client } from '@/types';
import type {
  ConversationDetail,
  ConversationSummary,
  ParsedTraceEvent,
  ProxyRequestEvent,
} from '@/types/publicDemoConversation';

/**
 * The public demo's conversations, read from the client's account through the
 * client access Lambda (TASK-625). The Lambda lists the demo user's traces,
 * runs the proxy log query, and signs a link to one trace at a time.
 */

const DEMO_USER_SUB = 'public-demo-user';

/** List every public-demo conversation, newest first. LastModified comes from S3 directly. */
export async function listPublicDemoConversations(client: Client): Promise<ConversationSummary[]> {
  const { conversations } = await clientAccess<{ conversations: ConversationSummary[] }>(
    'publicDemo.listConversations',
    { clientName: client.name }
  );
  return conversations;
}

export async function fetchConversationTrace(client: Client, conversationId: string): Promise<ConversationDetail> {
  const text = await fetchTraceText(client.name, DEMO_USER_SUB, conversationId);
  const parsed: ParsedTraceEvent[] = [];
  for (const line of text.split('\n')) {
    if (!line.trim()) continue;
    const ev = parseTraceLine(line);
    if (ev) parsed.push(ev);
  }
  const events = compactAssistantRuns(parsed);
  const firstUserMessage = events.find((e) => e.kind === 'user')?.text;
  return { conversationId, events, firstUserMessage };
}

// ─── Proxy-log IP enrichment ──────────────────────────────────────────
// The container trace doesn't include caller IPs. The public-demo-proxy
// Lambda logs each POST with the caller IP in the uvicorn access line:
//   `INFO:     49.224.117.149:0 - "POST /api/public-demo/invocations HTTP/1.1" 200 OK`
// The Lambda runs one Logs Insights query over the window, and the portal
// matches each conversation's S3 LastModified timestamp to the nearest
// proxy event.

/**
 * Fetch all proxy POSTs in the window, with timestamp and caller IP, sorted
 * by timestamp ascending so binary-searching callers is cheap.
 */
export async function fetchProxyRequestEvents(client: Client, daysBack = 30): Promise<ProxyRequestEvent[]> {
  const { events } = await clientAccess<{ events: ProxyRequestEvent[] }>('publicDemo.proxyEvents', {
    clientName: client.name,
    daysBack,
  });
  return events;
}

/**
 * Pick the single IP that best represents a conversation, given all proxy
 * events from the window. We search for any proxy event within ±5 minutes of
 * the conversation's S3 LastModified, take the most common IP among matches,
 * and fall back to the nearest single event.
 */
function correlateIp(conversationTs: string, proxyEvents: ProxyRequestEvent[]): string | undefined {
  if (proxyEvents.length === 0 || !conversationTs) return undefined;
  const target = new Date(conversationTs).getTime();
  if (!Number.isFinite(target)) return undefined;

  const WINDOW_MS = 5 * 60 * 1000;
  const withinWindow: ProxyRequestEvent[] = [];
  let nearest: ProxyRequestEvent | undefined;
  let nearestDelta = Infinity;

  for (const ev of proxyEvents) {
    const t = new Date(ev.timestamp).getTime();
    const delta = Math.abs(t - target);
    if (delta < nearestDelta) {
      nearestDelta = delta;
      nearest = ev;
    }
    if (delta <= WINDOW_MS) withinWindow.push(ev);
  }

  if (withinWindow.length > 0) {
    // Mode of IPs in the window.
    const counts = new Map<string, number>();
    for (const ev of withinWindow) counts.set(ev.ip, (counts.get(ev.ip) ?? 0) + 1);
    let bestIp: string | undefined;
    let bestCount = 0;
    for (const [ip, count] of counts) {
      if (count > bestCount) {
        bestCount = count;
        bestIp = ip;
      }
    }
    return bestIp;
  }

  // No events in window — fall back to the absolute nearest if it's within 30 min.
  if (nearest && nearestDelta <= 30 * 60 * 1000) return nearest.ip;
  return undefined;
}

/** Enrich a list of conversation summaries with best-effort source IPs. */
export function attachIpsToConversations(
  conversations: ConversationSummary[],
  proxyEvents: ProxyRequestEvent[]
): ConversationSummary[] {
  return conversations.map((c) => ({
    ...c,
    sourceIp: correlateIp(c.lastModified, proxyEvents),
  }));
}
